import { apiErrorMessage } from '@agos/api-client'
import type { DeliverySettingsRequest } from '@agos/api-client/model'
import {
  getGetSettingsDeliveryQueryKey,
  useGetSettingsDelivery,
  usePutSettingsDelivery,
} from '@agos/api-client/platform'
import { T, tt, useFranchiseModules } from '@agos/shared'
import { BUTTON_PRIMARY, cn, ConfirmDialog, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Bike, Loader2, Save } from 'lucide-react'
import { useState } from 'react'

import {
  COURIER_FIELD_LABELS,
  courierFormOf,
  courierFormProblem,
  courierPatchOf,
  modeNote,
  rewardExamples,
  type CourierForm,
  type DeliveryMode,
} from './courier-settings'

/**
 * *Externí šoféri* on *Nastavenia rozvozu* - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 A, owner O139. The rules
 * are `courier-settings.ts`.
 *
 * **It saves only its own key.** `PUT /settings/delivery` keeps the depot and the map when the request does not carry
 * them (the fix that came with this section), so this card and the depot card above cannot overwrite each other.
 *
 * **Drawn only for a franchise with the external couriers' module**, and only when the server answers the section -
 * a backend older than §10A.438 would save it into nothing.
 */
export function CourierSettingsSection() {
  const { isModuleEnabled } = useFranchiseModules()
  const { data, isLoading, isError } = useGetSettingsDelivery()
  if (!isModuleEnabled('external_driver') || isLoading || isError) return null
  const stored = courierFormOf(data)
  if (stored === null) return null
  // Keyed on what is stored, so a save that the server normalises (2,5 -> 2.50) refills the form from its answer.
  return <CourierSettingsForm key={JSON.stringify(stored)} stored={stored} />
}

function CourierSettingsForm({ stored }: { stored: CourierForm }) {
  const queryClient = useQueryClient()
  const save = usePutSettingsDelivery()
  const [form, setForm] = useState<CourierForm>(stored)
  const [confirming, setConfirming] = useState(false)
  const problem = courierFormProblem(form)
  const examples = rewardExamples(form)
  const changed = JSON.stringify(form) !== JSON.stringify(stored)
  // Turning delivery over to the couriers changes what every web customer pays for delivery, so it is asked once.
  const handsOver = form.mode === 'external' && stored.mode !== 'external'

  const set = (field: keyof CourierForm, value: string) => setForm((current) => ({ ...current, [field]: value }))

  const write = async () => {
    setConfirming(false)
    if (problem !== null) return
    try {
      /* The generated `DeliverySettingsRequest` predates §10A.438 and carries only optional fields, so TypeScript's
       * weak-type check refuses an object sharing none of them - §9.509's named variable is not enough here. Typed as
       * the request plus an index signature: no cast, nothing under `src/generated` touched, and the server has
       * accepted `externalDelivery` since §10A.438 and validates it. */
      const patch: DeliverySettingsRequest & Record<string, unknown> = { externalDelivery: courierPatchOf(form) }
      await save.mutateAsync({ data: patch })
      await queryClient.invalidateQueries({ queryKey: getGetSettingsDeliveryQueryKey() })
      toast({ title: tt('Uložené'), description: tt('Nastavenia externých šoférov boli uložené') })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Nepodarilo sa uložiť'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
  }

  const box = (field: Exclude<keyof CourierForm, 'mode'>) => (
    <label key={field} className="block">
      <span className="block text-xs font-medium text-foreground mb-1">{COURIER_FIELD_LABELS[field]}</span>
      <input
        data-courier-setting={field}
        inputMode="decimal"
        value={form[field]}
        onChange={(event) => set(field, event.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
      />
    </label>
  )

  const modeButton = (mode: DeliveryMode, label: string) => (
    <button
      type="button"
      data-delivery-mode={mode}
      aria-pressed={form.mode === mode}
      onClick={() => setForm((current) => ({ ...current, mode }))}
      className={cn(
        'flex-1 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors',
        form.mode === mode ? 'bg-primary/15 border-primary text-primary' : 'border-border text-muted-foreground',
      )}
    >
      {label}
    </button>
  )

  return (
    <div className="rounded-xl border border-border bg-card" data-courier-settings>
      <div className="px-5 py-4 border-b border-border bg-muted/30 rounded-t-xl">
        <h2 className="font-semibold text-foreground flex items-center gap-2">
          <Bike className="w-4 h-4 text-primary" />
          <T>Externí šoféri</T>
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          <T>
            Odmena šoféra je zároveň cena dopravy pre zákazníka. Šofér pri vyzdvihnutí zaplatí hotovostnú objednávku
            bez svojej odmeny; pri zaplatenej mu odmenu vyplatíte vy.
          </T>
        </p>
      </div>

      <div className="p-5 space-y-5">
        <div>
          <span className="block text-sm font-medium text-foreground mb-1.5">
            <T>Rozvoz zabezpečujú</T>
          </span>
          <div className="flex gap-2">
            {modeButton('own', tt('Vlastní šoféri'))}
            {modeButton('external', tt('Iba externí šoféri'))}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5" data-delivery-mode-note>
            {modeNote(form.mode)}
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-foreground mb-1.5">
            <T>Odmena šoféra</T>
          </span>
          <div className="grid gap-3 sm:grid-cols-3">
            {box('rewardBase')}
            {box('rewardPerKm')}
            {box('rewardMinimum')}
          </div>
          {examples.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1.5" data-reward-examples>
              <T>Napríklad</T>: {examples.join(' · ')}
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {box('maxKm')}
          {box('minimumOrder')}
        </div>

        <div>
          <span className="block text-sm font-medium text-foreground mb-1.5">
            <T>Kedy bude objednávka pripravená</T>
          </span>
          <div className="grid gap-3 sm:grid-cols-3">
            {box('pickupPiecesThreshold')}
            {box('pickupSmallMinutes')}
            {box('pickupLargeMinutes')}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            <T>
              Šofér vidí čas od prijatia do kuchyne. Pred prijatím vidí Čaká na prijatie do kuchyne. Počítajú sa len
              kusy z kuchyne, nie nápoje.
            </T>
          </p>
        </div>

        {problem !== null && changed && (
          <p className="text-xs text-red-500 font-medium" data-courier-settings-problem>
            {problem}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            data-courier-settings-save
            onClick={() => (handsOver ? setConfirming(true) : void write())}
            disabled={!changed || problem !== null || save.isPending}
            className={cn('inline-flex items-center gap-2 px-5 py-2.5 rounded-lg', BUTTON_PRIMARY, 'text-sm')}
          >
            {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <T>Uložiť</T>
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void write()}
        title={tt('Rozvoz iba cez externých šoférov?')}
        description={tt(
          'Od uloženia sa doprava na webe počíta z odmeny šoféra a vzdialenosti, nie zo zón, a každá objednávka na doručenie sa hneď ponúkne externým šoférom.',
        )}
        confirmLabel={tt('Áno, prepnúť')}
        cancelLabel={tt('Zrušiť')}
      />
    </div>
  )
}
